import React, {
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import ButtonGroup from '@mui/material/ButtonGroup';
import {
  useGetPastVisitNotesForPatient,
} from 'pages/Dashboard/pages/Encounters/pages/VisitNote/hooks';
import ToolTip from 'core/components/Tooltip';
import SaltIcon from 'core/icons/salt';
import KeyboardArrowDownRoundedIcon from '@mui/icons-material/KeyboardArrowDownRounded';
import Spinner from 'pages/Dashboard/components/Spinner';
import isNil from 'lodash/isNil';
import clsx from 'clsx';
import SaltMenu, {
  SaltType,
} from 'pages/Dashboard/pages/Encounters/pages/VisitNote/components/VisitNoteSalt/SaltMenu';
import head from 'lodash/head';
import Button from '@mui/material/Button';
import {
  getsSaltButtonDisabledTooltip,
  isSaltContentAvailable,
} from 'pages/Dashboard/pages/Encounters/pages/VisitNote/components/VisitNoteSalt/helper';

type Props = {
  patientId: number;
  visitNoteId: number;
  isSaltInProgress: boolean;
  saltType: SaltType;
  handleSalt: (selectedVisitNoteId: number | null) => void;
};

export default function SaltIconButton({
  patientId,
  visitNoteId,
  isSaltInProgress,
  saltType,
  handleSalt,
}: Props) {
  const [selectedVisitNoteId, setSelectedVisitNoteId] = useState<number | null>(null);

  const {
    data: pastVisitNotes = [],
    isLoading: loadingPastVisitNotes,
    isFetched: fetchedPastVisitNotes,
  } = useGetPastVisitNotesForPatient(patientId, visitNoteId);

  const hasPastVisitNotes = useMemo(() => pastVisitNotes?.length > 0, [pastVisitNotes]);
  const hasSelectedVisitNoteId = useMemo(() => !isNil(selectedVisitNoteId), [selectedVisitNoteId]);

  const mostRecentValidVisitNoteId = useMemo(
    () => head(pastVisitNotes.filter((v) => isSaltContentAvailable(v, saltType)))?.visitNoteId,
    [pastVisitNotes, saltType],
  );

  const hasValidPastVisitNote = !isNil(mostRecentValidVisitNoteId);

  const anchorRef = useRef<HTMLButtonElement>(null);
  const [anchorElement, setAnchorElement] = useState<HTMLElement | null>(null);

  const handleOpen = () => {
    setAnchorElement(anchorRef.current);
  };

  const handleClose = () => {
    setAnchorElement(null);
  };

  useEffect(() => {
    if (hasSelectedVisitNoteId) {
      handleClose();
      handleSalt(selectedVisitNoteId);
      setSelectedVisitNoteId(null);
    }
  }, [selectedVisitNoteId]);

  const tooltipMessage = useMemo(() => {
    let message = 'Same as Last Time';

    if (!fetchedPastVisitNotes) {
      message = 'Same as Last Time';
    } else if (!hasPastVisitNotes) {
      message = 'There is no prior visit note';
    } else if (!hasValidPastVisitNote) {
      message = getsSaltButtonDisabledTooltip(saltType);
    }

    return message;
  }, [fetchedPastVisitNotes, hasPastVisitNotes, hasValidPastVisitNote]);

  const disablingFactors = [
    isSaltInProgress,
    loadingPastVisitNotes,
    !hasPastVisitNotes,
    !hasValidPastVisitNote,
  ];

  const disabled = useMemo(
    () => disablingFactors.some((i) => i),
    [...disablingFactors],
  );

  return (
    <ToolTip title={tooltipMessage}>
      <span>
        <ButtonGroup
          variant="text"
          disableElevation
          className={clsx(
            '!bg-transparent !shadow-none border-0 !text-inherit',
            'flex items-center gap-0',
            { 'cursor-not-allowed': disabled },
          )}
          sx={{
            '& .MuiButtonGroup-grouped': {
              border: 'none !important',
            },
          }}
        >
          <Button
            ref={anchorRef}
            disabled={disabled}
            onClick={() => {
              setSelectedVisitNoteId(mostRecentValidVisitNoteId ?? null);
            }}
            className="!text-inherit !min-w-0 !p-1 !pr-0 !m-0 rounded-none disabled:opacity-60"
          >
            {isSaltInProgress ? (
              <Spinner />
            ) : (
              <SaltIcon className="!w-4 !h-4 text-gray-500" />
            )}
          </Button>

          <Button
            aria-haspopup="menu"
            disabled={disabled}
            onClick={handleOpen}
            className="!text-inherit !min-w-0 !p-1 !pl-0 !m-0 rounded-none disabled:opacity-60"
          >
            {loadingPastVisitNotes ? (
              <Spinner />
            ) : (
              <KeyboardArrowDownRoundedIcon className="!w-4 !h-4 text-gray-500" />
            )}
          </Button>
        </ButtonGroup>
        <SaltMenu
          pastVisitNotes={pastVisitNotes}
          anchorElement={anchorElement}
          saltType={saltType}
          onClose={handleClose}
          onClickMenuItem={setSelectedVisitNoteId}
        />
      </span>
    </ToolTip>
  );
}
